import { useCallback, useEffect, useRef } from 'react';

/**
 * Batch pagination that fetches the next page as the reader nears the bottom.
 *
 * The observer only AUTOMATES the "load more" button the caller keeps in its
 * markup -- that button stays the real control. A keyboard user tabs to it; a
 * reader who never scrolls the sentinel into view still has it; jsdom and old
 * browsers, which have no `IntersectionObserver`, fall back to it silently.
 * Infinite scroll with no such control is a list a keyboard cannot reach the
 * end of.
 *
 * `scrollRef` must be attached to the SCROLL CONTAINER, not left on the
 * viewport: the sentinel sits inside a fixed-height box, and measured against
 * the viewport it would report whatever that box's position happens to be and
 * never track the rows. `sentinelRef` goes on an empty element after the rows.
 *
 * Extracted from `pages/cade/CasesPage.tsx` on 2026-09-14 when the parties
 * directory and the fines ledger needed the same mechanism: one copy, so a
 * fix to the observer reaches every batched list.
 */
export function useAutoLoadMore({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined' || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      {
        root: scrollRef.current,
        // Fetch just before the sentinel is visible, so the next batch is
        // usually already in place by the time the reader reaches the bottom.
        rootMargin: '300px',
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, loadMore]);

  return { sentinelRef, scrollRef, loadMore };
}
